/**
 * Authentication Context
 * 
 * Provides authentication state and methods throughout the app
 */

import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authService } from '../services/auth';
import rbacService from '../services/rbac';
import { isAviatoPlatformOwner } from '../utils/platformOwnerIdentity';

const AuthContext = createContext(null);
let bootstrapRefreshPromise = null;
let bootstrapRefreshCompleted = false;
const AUTH_STORAGE_KEYS = new Set([
  'access_token',
  'refresh_token',
  'user',
  'user_profile',
]);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessMap, setAccessMap] = useState(null);

  const refetchAccess = useCallback(async () => {
    const accessTokenAtStart = localStorage.getItem('access_token') || '';
    try {
      const data = await rbacService.getMyAccess();
      if ((localStorage.getItem('access_token') || '') !== accessTokenAtStart) {
        return;
      }
      setAccessMap(data?.access || null);
    } catch {
      if ((localStorage.getItem('access_token') || '') !== accessTokenAtStart) {
        return;
      }
      setAccessMap(null); // fall back to hardcoded matrix
    }
  }, []);

  const refreshUser = async ({ silent = false } = {}) => {
    const currentUser = authService.getCurrentUser();
    setUser(currentUser);

    // If authenticated, refresh profile to ensure role/permissions are up to date.
    if (currentUser) {
      try {
        const accessTokenAtStart = localStorage.getItem('access_token') || '';
        const profile = await authService.getProfile();
        const accessTokenNow = localStorage.getItem('access_token') || '';
        if (accessTokenAtStart && accessTokenNow && accessTokenAtStart !== accessTokenNow) {
          const latestUser = authService.getCurrentUser();
          setUser(latestUser);
          return latestUser;
        }
        const storedProfile = authService.storeCurrentProfile(profile, accessTokenNow || accessTokenAtStart);
        const nextUser = storedProfile || authService.getCurrentUser();
        setUser(nextUser);
        refetchAccess();
        return nextUser;
      } catch (e) {
        // If token is invalid for this backend, force a clean login.
        const status = e?.response?.status;
        if (status === 401 || status === 403) {
          console.warn('[AuthProvider] Auth invalid for current API. Forcing logout.');
          authService.logout();
          setUser(null);
          window.location.href = '/login';
          return null;
        }
        // Non-fatal; keep whatever we have.
        if (!silent) {
          console.warn('[AuthProvider] Failed to refresh profile:', e);
        }
      }
    }

    return currentUser;
  };

  useEffect(() => {
    // Check the local session synchronously so the authenticated shell does
    // not wait on /users/profile/. A slow profile refresh should update roles
    // in the background, not hold every page behind a 30s timeout.
    const bootstrap = () => {
      const currentUser = authService.getCurrentUser();
      setUser(currentUser);
      setLoading(false);

      if (!currentUser) {
        return;
      }

      refetchAccess();

      if (bootstrapRefreshCompleted) {
        return;
      }

      if (!bootstrapRefreshPromise) {
        bootstrapRefreshPromise = refreshUser({ silent: true })
          .finally(() => {
            bootstrapRefreshCompleted = true;
            bootstrapRefreshPromise = null;
          });
      }

      bootstrapRefreshPromise.catch(() => {
        // refreshUser handles invalid-token logout and logs non-fatal failures.
      });
    };

    bootstrap();
    // Mount-only bootstrap: refreshUser/refetchAccess are stable for this effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const syncSessionFromAnotherTab = (event) => {
      if (event.storageArea && event.storageArea !== window.localStorage) {
        return;
      }
      if (event.key !== null && !AUTH_STORAGE_KEYS.has(event.key)) {
        return;
      }

      // `storage` fires in the other tabs after localStorage already contains
      // the new values. Re-read through the same token/profile validation used
      // at bootstrap, clear the previous account's RBAC map immediately, and
      // let ProtectedRoute's identity key remount the protected subtree.
      const currentUser = authService.getCurrentUser();
      setAccessMap(null);
      setUser(currentUser);
      setLoading(false);

      // Permissions belong to the session too. Refresh them in the background;
      // the user/header update above never waits for this request.
      if (currentUser) {
        refetchAccess();
      }
    };

    window.addEventListener('storage', syncSessionFromAnotherTab);
    return () => window.removeEventListener('storage', syncSessionFromAnotherTab);
  }, [refetchAccess]);

  const login = async (username, password, tenantSchema = '') => {
    const data = await authService.login(username, password, tenantSchema);
    // When MFA is required the backend returns an envelope with no tokens; do
    // not set a user yet — the caller drives the second step and then calls
    // completeMfaLogin below.
    if (data && data.mfaRequired) {
      return data;
    }
    setUser(authService.getCurrentUser());
    return data;
  };

  const completeMfaLogin = async (args) => {
    const data = await authService.completeMfaLogin(args);
    setUser(authService.getCurrentUser());
    return data;
  };

  /**
   * Install a JWT pair already issued by the backend (e.g. by
   * /auth/claim-invite/) without round-tripping through /auth/login/.
   * Used by the post-checkout activation flow so the freshly-provisioned
   * tenant_schema claim is honoured immediately.
   */
  const installSession = async ({ access, refresh, user: profile } = {}) => {
    await authService.installSession({ access, refresh, user: profile });
    setUser(authService.getCurrentUser());
  };

  const logout = () => {
    authService.logout();
    setUser(null);
    window.location.href = '/login';
  };

  const register = async (userData) => {
    const data = await authService.register(userData);
    return data;
  };

  const value = {
    user,
    loading,
    login,
    completeMfaLogin,
    logout,
    register,
    installSession,
    refreshUser,
    accessMap,
    pageLevel: (pageKey) => accessMap?.[pageKey] ?? null,
    refetchAccess,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isAviatoAdmin: isAviatoPlatformOwner(user),
    isPilot: user?.role === 'pilot',
    isMechanic: user?.role === 'mechanic',
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
